const CURRENCY_SYMBOLS: Record<string, string> = {
  INR: '₹',
  USD: '$',
  GBP: '£',
  EUR: '€',
  AUD: '$',
  CAD: '$',
  JPY: '¥',
};

/** ISO 4217 code → display symbol (falls back to code). */
export function symbolForCurrency(currencyCode: string): string {
  const code = currencyCode.trim().toUpperCase();
  return CURRENCY_SYMBOLS[code] ?? code;
}

/** When DB currency is unset, infer from country (Indian schools → INR). */
export function currencyCodeFromProfile(
  currencyCode?: string | null,
  countryCode?: string | null
): string {
  const country = countryCode?.trim().toUpperCase();
  const explicit = currencyCode?.trim().toUpperCase();
  // Dev/demo tenants often seed USD while country is IN — show INR in the UI.
  if (country === 'IN' && (!explicit || explicit === 'USD')) {
    return 'INR';
  }
  if (explicit) return explicit;
  if (country === 'IN') return 'INR';
  return 'USD';
}

/** Minor-unit label for payment prompts (API amounts are still in minor units). */
export function minorUnitLabel(currencyCode: string): string {
  const code = currencyCode.trim().toUpperCase();
  if (code === 'INR') return 'paise';
  if (code === 'JPY') return 'yen (whole units use minor field as configured)';
  return 'cents';
}

/** Convert a major-unit amount (e.g. rupees) to minor units stored by the API (paise/cents). */
export function majorAmountToMinorUnits(amount: number, currencyCode?: string): number {
  const code = currencyCode?.trim().toUpperCase();
  if (code === 'JPY') return Math.round(amount);
  return Math.round(amount * 100);
}

/** Convert API minor units to a major-unit number for form inputs. */
export function minorUnitsToMajorAmount(cents: number, currencyCode?: string): number {
  const code = currencyCode?.trim().toUpperCase();
  if (code === 'JPY') return Number(cents);
  return Number(cents) / 100;
}

/** Format minor units (cents/paise) for display. */
export function formatMoneyCents(
  cents: number,
  currencySymbol?: string,
  currencyCode?: string
): string {
  const amount = minorUnitsToMajorAmount(cents, currencyCode).toFixed(2);
  const code = currencyCode?.trim().toUpperCase();
  const sym = (currencySymbol?.trim() || (code ? symbolForCurrency(code) : '₹')).trim();
  const suffix =
    code && sym !== code && sym !== symbolForCurrency(code) ? ` ${code}` : '';
  if (sym.length === 1 || sym === '₹' || sym === '¥') {
    return `${sym}${amount}${suffix}`;
  }
  return `${sym} ${amount}${suffix}`.trim();
}
